import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, Users, Repeat, ScrollText } from 'lucide-react';
import type { MesaVista, PedidoVista } from '@shared/types';
import { useNegocioStore } from '@renderer/store/useNegocioStore';
import { puedeUsarCanal } from '@shared/permisos';
import { useAjustesPermisos } from '@renderer/store/useAjustesPermisos';
import { useSessionStore } from '@renderer/store/useSessionStore';
import { CobroModal } from '@renderer/components/cobro/CobroModal';
import { TicketPreviewModal } from '@renderer/components/ticket/TicketPreviewModal';
import { traducirErrorBackend } from '@renderer/lib/mensajesError';
import { MesasGrid } from '@renderer/components/mesas/MesasGrid';
import { MoverMesaModal } from '@renderer/components/pedidos/MoverMesaModal';
import { JuntarMesasModal } from '@renderer/components/pedidos/JuntarMesasModal';
import { DividirCuentaModal } from '@renderer/components/pedidos/DividirCuentaModal';
import { LiberarMesaModal } from '@renderer/components/mesas/LiberarMesaModal';
import { HelpButton } from '@renderer/components/help/HelpButton';
import { tourMesas } from '@renderer/tours';
import { EntregarTurnoModal, mesasEntregables } from '@renderer/components/mesas/EntregarTurnoModal';

const REFRESCO_MS = 5_000;

/**
 * Sprint 7.2: MesasPage operativa.
 * Sprint 9.0: refactor para consumir `<MesasGrid mode="monitor" />`.
 * Sprint 9.2: kebab con acciones contextuales (mover/juntar/dividir/liberar)
 * + estados "fuera de servicio" / "reactivar".
 */
export function MesasPage() {
  const navigate = useNavigate();
  const sesion = useSessionStore((s) => s.sesion);
  const negocio = useNegocioStore((s) => s.negocio);
  const monedaSimbolo = negocio?.monedaSimbolo ?? '$';
  const locale = negocio?.locale ?? 'es-MX';
  const esMesero = sesion?.rol === 'mesero';
  /**
   * Sprint 64 — Antes el botón "Cobrar" aparecía para todos y el mesero descubría que no
   * podía DESPUÉS de teclear el efectivo recibido. Ahora la pantalla pregunta lo mismo
   * que va a preguntar el servidor.
   *
   * Se suscribe a `ajustes` (no a `puede`, que es una función estable y no dispararía el
   * re-render): si el dueño enciende el interruptor, el botón aparece sin volver a entrar.
   * Esto es cortesía de interfaz, no control de acceso — quien autoriza es
   * `safeHandleConPermiso` en el servidor.
   */
  const ajustesPermisos = useAjustesPermisos((s) => s.ajustes);
  const puedeCobrar = puedeUsarCanal(sesion?.rol, 'pedidos:cobrar', ajustesPermisos);

  const [mesas, setMesas] = useState<MesaVista[]>([]);
  const [pedidos, setPedidos] = useState<PedidoVista[]>([]);
  const [cargando, setCargando] = useState(true);
  const [cobroId, setCobroId] = useState<number | null>(null);
  const [previewId, setPreviewId] = useState<number | null>(null);

  // Sprint 9.2 — Estado de modales contextuales
  const [moverState, setMoverState] = useState<{ pedidoId: number; mesaActualId: number } | null>(null);
  const [juntarState, setJuntarState] = useState<{ pedidoId: number; mesaPrincipalId: number } | null>(null);
  const [dividirState, setDividirState] = useState<number | null>(null);
  const [liberarState, setLiberarState] = useState<{ pedidoId: number; mesaNumero: number } | null>(null);
  // Sprint 172 — Reasignar las mesas de un mesero que ya se fue (caja / dueña, con su PIN).
  const [reasignando, setReasignando] = useState(false);
  const [avisoReasignacion, setAvisoReasignacion] = useState<string | null>(null);
  const puedeReasignar = !esMesero && puedeUsarCanal(sesion?.rol, 'pedidos:entregar-turno', ajustesPermisos);

  const cargar = async () => {
    const [m, p] = await Promise.all([
      window.api.mesas.listar(),
      window.api.pedidos.listarPorEstado([
        'abierto',
        'pendiente_cocina',
        'cocina',
        'listo',
        'entregado',
      ]),
    ]);
    if (m.ok) setMesas(m.data);
    if (p.ok) setPedidos(p.data);
    setCargando(false);
  };

  useEffect(() => {
    cargar();
    const t = setInterval(cargar, REFRESCO_MS);
    return () => clearInterval(t);
  }, []);

  const pedidoPorMesa = useMemo(() => {
    const mapa = new Map<number, PedidoVista>();
    for (const p of pedidos) {
      if (p.mesaId) mapa.set(p.mesaId, p);
      for (const id of p.mesaIds ?? []) {
        if (!mapa.has(id)) mapa.set(id, p);
      }
    }
    return mapa;
  }, [pedidos]);

  const disponibles = mesas.filter((m) => m.estado === 'disponible').length;
  const ocupadas = mesas.filter((m) => m.estado === 'ocupada').length;
  const fueraServicio = mesas.filter((m) => m.estado === 'fuera_de_servicio').length;
  const cocinaLista = [...pedidoPorMesa.values()].filter((p) => p.estado === 'listo').length;
  const cuentasPedidas = [...pedidoPorMesa.values()].filter((p) => p.cuentaSolicitada).length;

  /**
   * Sprint 111 — El flujo de cocina, desde la misma pantalla del salón.
   *
   * Petición del dueño: *"ella puede ver cuando algo ya está, avisarle al mesero que atiende la
   * mesa y ver cómo el mesero lleva el pedido; ese flujo debería poder administrarlo sin estar
   * cambiando de secciones"*.
   *
   * Aquí sí se usa `marcarListo` (que escribe `estado`) y no `fechaEmpaquetado` como en mostrador:
   * un pedido de mesa se cobra **al final**, así que cuando esto se toca su estado todavía es
   * `'pendiente_cocina'` o `'cocina'` — no hay ningún `'pagado'` que pisar. El propio servicio lo
   * rechaza si el pedido ya está pagado.
   */
  const marcarListo = async (pedidoId: number) => {
    const res = await window.api.pedidos.marcarListo(pedidoId);
    if (!res.ok) {
      window.alertar({
        titulo: 'No se pudo marcar como listo',
        mensaje: traducirErrorBackend(res.error, res.codigo),
        variant: 'warn',
      });
      return;
    }
    cargar();
  };

  /** "Ya se lo llevaron a la mesa": `listo → entregado`. Lo que faltaba para cerrar el ciclo aquí. */
  const entregarEnMesa = async (pedidoId: number) => {
    const res = await window.api.pedidos.marcarEntregadoMesa({ pedidoId });
    if (!res.ok) {
      window.alertar({
        titulo: 'No se pudo marcar como entregado',
        mensaje: traducirErrorBackend(res.error, res.codigo),
        variant: 'warn',
      });
      return;
    }
    cargar();
  };

  const marcarCuenta = async (pedidoId: number, solicitada: boolean) => {
    const res = await window.api.pedidos.marcarCuentaSolicitada({ pedidoId, solicitada });
    if (!res.ok) window.alertar({ titulo: 'Error', mensaje: res.error, variant: 'warn' });
    cargar();
  };

  // Sprint 29 — Volver a juntar una cuenta dividida desde la tarjeta de mesa.
  const reunificar = async (pedidoId: number) => {
    if (!sesion) return;
    const ok = await window.confirmar({
      titulo: 'Volver a juntar la cuenta',
      mensaje:
        'Los sub-pedidos de esta división se reunirán en una sola cuenta (el folio original).' +
        ' No se puede si alguno ya fue cobrado.',
      textoConfirmar: 'Sí, juntar',
      textoCancelar: 'Cancelar',
    });
    if (!ok) return;
    const res = await window.api.pedidos.reunificarCuenta({ pedidoId, usuarioId: sesion.usuarioId });
    if (!res.ok) {
      window.alertar({ titulo: 'No se pudo juntar', mensaje: res.error, variant: 'warn' });
      return;
    }
    cargar();
  };

  const handleNuevoPedido = (mesaId: number) => {
    navigate(`/pos?tipo=comer_aqui&mesa=${mesaId}`);
  };

  const handleFueraServicio = async (mesaId: number, mesaNumero: number) => {
    const ok = await window.confirmar({ titulo: 'Fuera de servicio', mensaje: `¿Marcar la mesa #${mesaNumero} como fuera de servicio? No aparecerá en el flujo de selección hasta que la reactives.`, textoConfirmar: 'Sí, retirar', variant: 'warn' });
    if (!ok) return;
    const res = await window.api.mesas.marcarFueraServicio(mesaId);
    if (!res.ok) window.alertar({ titulo: 'Error', mensaje: res.error, variant: 'warn' });
    cargar();
  };

  const handleReactivar = async (mesaId: number) => {
    const res = await window.api.mesas.reactivar(mesaId);
    if (!res.ok) window.alertar({ titulo: 'Error', mensaje: res.error, variant: 'warn' });
    cargar();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between" data-tour="mes-contadores">
        <div className="flex items-center gap-3">
          <Users className="h-7 w-7 text-primary" />
          <div>
            <h1 className="text-2xl font-bold">Mesas</h1>
            <p className="text-sm text-slate-500">
              {disponibles} libre{disponibles !== 1 ? 's' : ''} · {ocupadas} ocupada
              {ocupadas !== 1 ? 's' : ''}
              {fueraServicio > 0 && (
                <span className="ml-2 text-slate-600 font-medium">
                  · {fueraServicio} fuera de servicio
                </span>
              )}
              {cocinaLista > 0 && (
                <span className="ml-2 text-emerald-600 font-medium">
                  · {cocinaLista} cocina lista
                </span>
              )}
              {cuentasPedidas > 0 && (
                <span className="ml-2 text-rose-600 font-medium">
                  · {cuentasPedidas} pidió cuenta
                </span>
              )}
            </p>
          </div>
        </div>
        <div className="flex gap-2" data-tour="mes-acciones">
          <button onClick={cargar} className="btn-ghost" aria-label="Refrescar">
            <RefreshCw className="h-4 w-4" />
          </button>
          {puedeReasignar && (
            <>
              <button
                type="button"
                onClick={() => { setAvisoReasignacion(null); setReasignando(true); }}
                className="flex min-h-[44px] items-center gap-2 rounded-lg border-2 border-slate-300 px-4 text-base font-semibold text-slate-800 hover:border-primary"
              >
                <Repeat className="h-5 w-5" /> Pasar mesas
              </button>
              <button
                type="button"
                onClick={() => navigate('/cambios-de-turno')}
                className="flex min-h-[44px] items-center gap-2 rounded-lg border-2 border-slate-300 px-4 text-base font-semibold text-slate-800 hover:border-primary"
              >
                <ScrollText className="h-5 w-5" /> Cambios de turno
              </button>
            </>
          )}
          {!esMesero && (
            <button
              onClick={() => navigate('/settings?tab=mesas')}
              className="btn-secondary text-xs"
              title="Alta, edición y eliminación de mesas"
            >
              Administrar mesas
            </button>
          )}
          <HelpButton tour={tourMesas} />
        </div>
      </div>

      {avisoReasignacion && (
        <div role="status" className="rounded-xl border-2 border-emerald-300 bg-emerald-50 p-4 text-base font-medium text-emerald-900">
          {avisoReasignacion}
        </div>
      )}

      {cargando ? (
        <div className="text-center text-slate-400 py-12">Cargando...</div>
      ) : mesas.length === 0 ? (
        <div className="card text-center text-slate-400 py-12">
          <Users className="mx-auto mb-3 h-12 w-12 opacity-30" />
          <p>No hay mesas registradas.</p>
          {!esMesero && (
            <button
              onClick={() => navigate('/settings?tab=mesas')}
              className="btn-primary mt-3 mx-auto"
            >
              Crear la primera
            </button>
          )}
        </div>
      ) : (
        <div data-tour="mes-grid">
        <MesasGrid
          mesas={mesas}
          pedidoPorMesa={pedidoPorMesa}
          mode="monitor"
          monedaSimbolo={monedaSimbolo}
          locale={locale}
          onMesaLibre={handleNuevoPedido}
          onMarcarListo={marcarListo}
          onEntregarEnMesa={entregarEnMesa}
          onCobrar={puedeCobrar ? setCobroId : undefined}
          onVerTicket={setPreviewId}
          onMarcarCuenta={marcarCuenta}
          onAgregarProductos={(pedidoId) => navigate(`/pos?agregar=${pedidoId}`)}
          onReunificarCuenta={reunificar}
          onMoverPedido={(pedidoId, mesaActualId) => setMoverState({ pedidoId, mesaActualId })}
          onJuntarMesa={(pedidoId, mesaPrincipalId) => setJuntarState({ pedidoId, mesaPrincipalId })}
          onDividirCuenta={setDividirState}
          onLiberarMesa={(pedidoId, mesaNumero) => setLiberarState({ pedidoId, mesaNumero })}
          onFueraServicio={handleFueraServicio}
          onReactivar={handleReactivar}
        />
        </div>
      )}

      <CobroModal
        abierto={cobroId !== null}
        pedidoId={cobroId}
        onClose={() => setCobroId(null)}
        onCobrado={() => {
          setCobroId(null);
          cargar();
        }}
      />

      <TicketPreviewModal
        abierto={previewId !== null}
        pedidoId={previewId}
        pdfPath={null}
        onClose={() => setPreviewId(null)}
        // Sprint 168 — Desde la cuenta impresa, cobrar es el botón principal.
        onCobrar={
          puedeCobrar
            ? (id) => {
                setPreviewId(null);
                setCobroId(id);
                cargar();
              }
            : undefined
        }
      />

      <MoverMesaModal
        abierto={moverState !== null}
        pedidoId={moverState?.pedidoId ?? null}
        mesaActualId={moverState?.mesaActualId ?? null}
        onClose={() => setMoverState(null)}
        onMovido={() => {
          setMoverState(null);
          cargar();
        }}
      />

      <JuntarMesasModal
        abierto={juntarState !== null}
        pedidoId={juntarState?.pedidoId ?? null}
        mesaPrincipalId={juntarState?.mesaPrincipalId ?? null}
        mesasYaUnidas={
          juntarState
            ? (pedidos.find((p) => p.id === juntarState.pedidoId)?.mesaIds ?? []).filter(
                (id) => id !== juntarState.mesaPrincipalId,
              )
            : []
        }
        onClose={() => setJuntarState(null)}
        onUnido={() => {
          setJuntarState(null);
          cargar();
        }}
      />

      <DividirCuentaModal
        abierto={dividirState !== null}
        pedidoId={dividirState}
        onClose={() => setDividirState(null)}
        onDividido={() => {
          setDividirState(null);
          cargar();
        }}
      />

      <EntregarTurnoModal
        abierto={reasignando}
        modo="reasignar"
        mesas={mesasEntregables(mesas, pedidos)}
        onCerrar={() => setReasignando(false)}
        onListo={(m) => {
          setReasignando(false);
          setAvisoReasignacion(m);
          cargar();
        }}
      />

      <LiberarMesaModal
        abierto={liberarState !== null}
        pedidoId={liberarState?.pedidoId ?? null}
        mesaNumero={liberarState?.mesaNumero ?? null}
        onClose={() => setLiberarState(null)}
        onLiberado={() => {
          setLiberarState(null);
          cargar();
        }}
      />
    </div>
  );
}
